import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import Logo from './logo.webp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { Menu } from '@headlessui/react';
import { setUser } from '../../store/userSlice';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { UserLocalStorageKeysEnum, UserRoleEnum } from '../../interfaces/user.interface';
import { TokenCookieManager } from '../../utils/tokenCookies';
import { adminPageLabel } from './adminNav';

interface SidebarProps {
  onOptionClick: (option: string) => void;
  selectedOption: string;
}

const Sidebar = ({ onOptionClick, selectedOption }: SidebarProps) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const [openMenu, setOpenMenu] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  type NavSection = { heading: string; items: string[] };

  const sections: NavSection[] = (() => {
    switch (user?.role) {
      case UserRoleEnum.customerService:
        return [{ heading: '', items: ['Customers'] }];
      case UserRoleEnum.admin:
        return [{ heading: '', items: ['Customers', 'All Orders', 'Print Batches', 'Order Recovery', 'Error Logs'] }];
      case UserRoleEnum.superAdmin:
        return [
          { heading: '', items: ['Dashboard'] },
          { heading: 'People', items: ['Customers', 'Students', 'Admin Users'] },
          { heading: 'Schoolwork', items: ['Classrooms'] },
          { heading: 'Operations', items: ['All Orders', 'Print Batches', 'Order Recovery'] },
          { heading: 'Contents', items: ['Art Styles', 'Training Videos', 'Projects', 'Reading Levels', 'Districts', 'Schools', 'Countries'] },
          { heading: 'Config', items: ['Plans', 'Coupons', 'Settings'] },
          { heading: 'Logs', items: ['Mid Journey Stats', 'Book Generations', 'Marketing Audit', 'Error Logs'] },
        ];
      default:
        return [];
    }
  })();
  const options = sections.flatMap((s) => s.items);
  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        dispatch(setUser(null));
        TokenCookieManager.removeToken();
        localStorage.removeItem(UserLocalStorageKeysEnum.CURRENT_USER);
        navigate('/');
        return new Promise<void>((resolve) => {
          setTimeout(() => {
            window.location.reload();
            resolve();
          }, 0);
        });
      })
      .catch((error) => {});
  };
  return (
    <>
      <div className="sm:hidden bg-white flex p-4 items-center justify-between min-h-screen">
        <Menu>
          <Menu.Button className="rounded-full focus:outline-none">
            <FontAwesomeIcon
              icon={faBars}
              className="cursor-pointer text-storyblue text-3xl hover:text-storygreen"
              title="Menu"
              onClick={() => setOpenMenu(!openMenu)}
            />
          </Menu.Button>
          {openMenu && (
            <Menu.Items className="absolute top-[3rem] left-[1rem] w-44 z-0 origin-top-right bg-gray-100 divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {sections.map(({ heading, items }) => (
                <React.Fragment key={heading || items[0]}>
                  {heading && (
                    <div className="px-3 pt-4 pb-0 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {heading}
                    </div>
                  )}
                  {items.map((option) => (
                    <Menu.Item key={option}>
                      {({ active }) => (
                        <div
                          className={`px-2 py-1 text-left rounded-md m-1 hover:bg-storytan text-[#062457] cursor-pointer ${
                            selectedOption === option ? 'font-bold bg-storytan' : ''
                          }`}
                          onClick={() => {
                            onOptionClick(option);
                            setOpenMenu(false);
                          }}
                        >
                          {adminPageLabel(option)}
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                </React.Fragment>
              ))}
            </Menu.Items>
          )}
        </Menu>
        <img className="h-10 md:h-12 w-auto" src={Logo} alt="Build My Story" />
        <button
          onClick={() => handleLogout()}
          className="flex justify-center bg-storyblue text-white font-bold rounded-md p-2"
        >
          Sign Out
        </button>
      </div>
      <aside className="hidden sm:flex bg-white text-gray-200 w-56 flex-col justify-between py-5 sticky top-0 self-start h-screen overflow-y-auto">
        <nav className="flex flex-col justify-between">
          <div className="flex items-center p-4">
            <img className="h-10 md:h-12 w-auto" src={Logo} alt="Build My Story" />
          </div>
          <h3 className="mx-auto text-storygreen mb-4">
            Welcome <b>{user?.firstName}!</b>
          </h3>
          <ul>
            {sections.map(({ heading, items }) => (
              <React.Fragment key={heading || items[0]}>
                {heading && (
                  <li className="px-3 pt-4 pb-0 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider select-none">
                    {heading}
                  </li>
                )}
                {items.map((option) => (
                  <li
                    key={option}
                    className={`py-1 text-left rounded-md hover:bg-storytan text-[#062457] cursor-pointer ${
                      heading ? 'px-4 mx-1 my-0.5' : 'px-2 mx-1 my-0.5'
                    } ${selectedOption === option ? 'font-bold bg-storytan' : ''}`}
                    onClick={() => onOptionClick(option)}
                  >
                    {adminPageLabel(option)}
                  </li>
                ))}
              </React.Fragment>
            ))}
          </ul>
        </nav>
        <button
          onClick={() => handleLogout()}
          className="flex justify-center bg-storyblue text-white font-bold rounded-md m-1 p-2"
        >
          Sign Out
        </button>
      </aside>
    </>
  );
};
export default Sidebar;
